import type { TFunction } from 'i18next';
import posthog from 'posthog-js';

import { toast } from '@feathr/components';
import { moment } from '@feathr/hooks';
import { findRachisError } from '@feathr/rachis';

/** `meta.code` the backend sends when Google's shared daily API limit is reached. */
export const GOOGLE_ADS_QUOTA_ERROR_CODE = 'google_ads_quota_exhausted';

export const GOOGLE_ADS_QUOTA_TOAST_ID = 'google-ads-quota-exhausted';

export function isGoogleAdsQuotaExhaustedError(error: unknown): boolean {
  return !!findRachisError(error, (entry) => entry.meta?.code === GOOGLE_ADS_QUOTA_ERROR_CODE);
}

/** What caused the toast: a page loading, or the Google Ads action that failed on the limit. */
export type TGoogleAdsQuotaToastTrigger =
  | 'page_load'
  | 'publish'
  | 'keyword_ideas'
  | 'location_search'
  | 'account_connect';

/**
 * Formats `reset_at` (an ISO datetime with no timezone, always UTC) as a local time-of-day
 * string, e.g. "1:26 PM". Returns null when `resetAt` is missing or fails to parse, so the
 * caller can fall back to time-agnostic copy instead of showing an invalid time.
 */
export function formatQuotaResetTime(resetAt: string | null | undefined): string | null {
  if (!resetAt) {
    return null;
  }

  const parsed = moment.utc(resetAt, moment.ISO_8601, true);
  if (!parsed.isValid()) {
    return null;
  }

  return parsed.local().format('h:mm A');
}

/** Shows the persistent limit-reached error toast. A fixed id keeps repeats from stacking. */
export function showGoogleAdsQuotaToast(
  t: TFunction,
  trigger: TGoogleAdsQuotaToastTrigger,
  resetAt?: string | null,
): void {
  posthog.capture('google_ads_quota_toast_shown', { trigger });
  const resetTime = formatQuotaResetTime(resetAt);
  const description =
    resetTime === null
      ? t(
          "Google limits daily Google Ads activity across all Feathr accounts, and today's limit has been reached. This isn't a problem with your account. Please check back later.",
        )
      : t(
          "Google limits daily Google Ads activity across all Feathr accounts, and today's limit has been reached. This isn't a problem with your account. Please check back after {{resetTime}}.",
          { resetTime },
        );
  toast(
    {
      title: t('Google Ads is temporarily unavailable'),
      description,
    },
    { type: 'error', toastId: GOOGLE_ADS_QUOTA_TOAST_ID },
  );
}
